import { useRef, useState } from "react";
import { CheckCircle2, FileCheck2, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, fmtDate, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Field, Input, Select, Textarea } from "@/components/common/Field";
import { FormDialog } from "@/components/vehicles/VehicleFormDialog";
import { EmptyState } from "@/components/common/Primitives";
import { DocumentList } from "@/pages/DocumentsPage";
import { FileField, docUrl, uploadDocument } from "@/lib/uploads";

const TYPES = { insurance: "Insurance", puc: "PUC", fitness: "Fitness", tax: "Road Tax", permit: "Permit", warranty: "Warranty" };

export function ComplianceTab({ vehicle, reload }) {
  const { can } = useAuth();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ type: "insurance" });
  const [verifying, setVerifying] = useState(null);
  const [vNote, setVNote] = useState("");
  const fileRef = useRef(null);
  const canEdit = can("admin", "fleet_manager", "garage_supervisor");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async () => {
    await api.post("/compliance", { ...f, vehicle_id: vehicle.id, amount: f.amount ? Number(f.amount) : null });
    toast.success("Compliance item added"); reload();
  };
  const verify = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) throw new Error("Attach the document (photo or PDF) to verify this record");
    const doc = await uploadDocument(file, { vehicle_id: vehicle.id, title: `${TYPES[verifying.type]}${verifying.reference_no ? ` ${verifying.reference_no}` : ""}`, category: verifying.type, expiry_date: verifying.expiry_date });
    await api.post(`/compliance/${verifying.id}/verify`, { document_id: doc.id, note: vNote || null });
    toast.success("Verified with document"); setVNote(""); reload();
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6" data-testid="compliance-overview">
        {Object.entries(TYPES).map(([k, label]) => (
          <div key={k} className="rounded-lg border px-3 py-2">
            <p className="fv-caption">{label}</p>
            <StatusBadge status={vehicle.compliance.by_type[k]} testId={`compliance-status-badge-${k}`} className="mt-1" />
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold">Records</h3>
        {canEdit && <button onClick={() => setOpen(true)} data-testid="add-compliance-btn" className="flex h-9 items-center gap-1 rounded-full bg-primary px-3 text-sm font-semibold text-primary-foreground hover:bg-[#0071e3]"><Plus className="h-4 w-4" />Add</button>}
      </div>
      {vehicle.compliance_items.length === 0 ? <EmptyState testId="compliance-empty" title="No compliance records" description="Add insurance, PUC, fitness, tax, permit or warranty records." /> : (
        <div className="fv-card divide-y p-1">
          {vehicle.compliance_items.map((it) => (
            <div key={it.id} className="fv-dense-row cursor-default" data-testid={`compliance-row-${it.id}`}>
              <div className="min-w-0 flex-1">
                <p className="font-semibold">{TYPES[it.type]} <span className="font-normal text-muted-foreground">· {it.provider || "—"}{it.reference_no ? ` · ${it.reference_no}` : ""}</span></p>
                <p className="text-[11px] text-muted-foreground">Expires {fmtDate(it.expiry_date)}{it.days_remaining != null ? ` (${it.days_remaining >= 0 ? `${it.days_remaining} days left` : `${-it.days_remaining} days ago`})` : ""}{it.is_verified ? <> · Verified {fmtDateTime(it.verified_on)}{it.document_id && <> · <a href={docUrl(it.document_id)} target="_blank" rel="noreferrer" className="text-primary hover:underline" data-testid={`verified-doc-link-${it.id}`}>View document</a></>}</> : " · Not verified"}</p>
              </div>
              <StatusBadge status={it.status} testId={`compliance-item-status-${it.id}`} />
              {canEdit && !it.is_verified && <button onClick={() => setVerifying(it)} data-testid={`verify-compliance-${it.id}`} className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-primary hover:bg-primary/10"><CheckCircle2 className="h-3.5 w-3.5" />Verify</button>}
            </div>
          ))}
        </div>
      )}
      <div className="border-t pt-4" data-testid="compliance-documents">
        <h3 className="mb-3 text-base font-semibold">Documents</h3>
        <DocumentList vehicleId={vehicle.id} />
      </div>
      <FormDialog open={open} onClose={() => setOpen(false)} title="Add compliance record" onSubmit={submit} testId="compliance-form-dialog">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Type" required><Select data-testid="compliance-type-select" value={f.type} onChange={set("type")}>{Object.entries(TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</Select></Field>
          <Field label="Provider / authority"><Input data-testid="compliance-provider-input" value={f.provider || ""} onChange={set("provider")} /></Field>
          <Field label="Reference / policy no."><Input data-testid="compliance-ref-input" value={f.reference_no || ""} onChange={set("reference_no")} /></Field>
          <Field label="Amount (₹)"><Input data-testid="compliance-amount-input" type="number" value={f.amount || ""} onChange={set("amount")} /></Field>
          <Field label="Issue date"><Input data-testid="compliance-issue-date-input" type="date" value={f.issue_date || ""} onChange={set("issue_date")} /></Field>
          <Field label="Expiry date" required><Input data-testid="compliance-expiry-date-input" type="date" value={f.expiry_date || ""} onChange={set("expiry_date")} required /></Field>
          <Field label="Notes" className="sm:col-span-2"><Textarea data-testid="compliance-notes-input" value={f.notes || ""} onChange={set("notes")} /></Field>
        </div>
      </FormDialog>
      <FormDialog open={!!verifying} onClose={() => setVerifying(null)} title={verifying ? `Verify ${TYPES[verifying.type]}` : ""} onSubmit={verify} submitLabel="Upload & verify" testId="verify-compliance-dialog">
        {verifying && (
          <div className="space-y-3">
            <p className="flex items-start gap-2 rounded-[11px] bg-secondary px-3 py-2 text-sm text-muted-foreground" data-testid="verify-hint"><FileCheck2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />Verification requires the actual document. Upload the {TYPES[verifying.type].toLowerCase()} certificate / policy (photo or PDF); it will be saved under this vehicle's documents.</p>
            <FileField label={`${TYPES[verifying.type]} document`} inputRef={fileRef} testId="verify-document-input" hint="Required · JPG, PNG or PDF" />
            <Field label="Note"><Input data-testid="verify-note-input" value={vNote} onChange={(e) => setVNote(e.target.value)} placeholder="Optional, e.g. checked against insurer portal" /></Field>
          </div>
        )}
      </FormDialog>
    </div>
  );
}
